import { Box, Stack, Typography } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import type { ReactNode } from 'react';

// Spec: design-system/proposals/2026-10-23-page-header-type-scale.md (A-2214).

export type PageHeaderCloud = 'account' | 'tools' | 'rewardsA' | 'rewardsB';

export interface PageHeaderProps {
  /** Standard on every page; campaign only for tournaments and time-boxed campaigns. */
  tier?: 'standard' | 'campaign';
  /** Section name (Account, Tools, Rewards, Tournaments), or a breadcrumb on detail pages. */
  eyebrow: ReactNode;
  /** The menu label. Rendered as the page's h1. */
  title: ReactNode;
  /** One sentence; may end with a Read more / Learn more link. */
  lead?: ReactNode;
  /** 0–3 live facts, e.g. small outlined `Chip`s. */
  chips?: ReactNode;
  /** 0–2 actions, primary first. Full width under the lead below `md`. */
  actions?: ReactNode;
  /** Hue follows the section, shape follows the page. Standard tier, and a campaign band without art. */
  cloud?: PageHeaderCloud;
  /** Campaign tier only: artwork exported on a transparent background. */
  art?: { src: string };
  /** Content container, matching the page's own (`pageContentSx`). */
  maxWidth?: number;
}

const mix = (role: 'main' | 'dark', percent: number) =>
  `color-mix(in srgb, var(--mui-palette-primary-${role}) ${percent}%, transparent)`;

const blob = (size: string, at: string, color: string) => `radial-gradient(${size} at ${at}, ${color}, transparent 72%)`;

const CLOUDS: Record<PageHeaderCloud, { xs: string; md: string }> = {
  account: {
    xs: [blob('80% 70%', '15% 0%', mix('dark', 40)), blob('50% 45%', '95% 25%', mix('main', 10))].join(', '),
    md: [blob('36% 72%', '20% 0%', mix('dark', 40)), blob('26% 50%', '80% 18%', mix('main', 10))].join(', '),
  },
  tools: {
    xs: [blob('75% 65%', '85% 0%', mix('main', 20)), blob('55% 50%', '5% 35%', mix('dark', 14))].join(', '),
    md: [blob('34% 70%', '76% 0%', mix('main', 20)), blob('28% 55%', '16% 30%', mix('dark', 14))].join(', '),
  },
  rewardsA: {
    xs: [blob('85% 75%', '25% 0%', mix('dark', 42)), blob('50% 50%', '95% 10%', mix('dark', 26))].join(', '),
    md: [blob('40% 80%', '28% 0%', mix('dark', 42)), blob('26% 60%', '84% 8%', mix('dark', 26))].join(', '),
  },
  rewardsB: {
    xs: [blob('100% 70%', '70% 0%', mix('dark', 38)), blob('50% 45%', '0% 30%', mix('main', 14))].join(', '),
    md: [blob('55% 78%', '60% 0%', mix('dark', 38)), blob('24% 50%', '10% 28%', mix('main', 14))].join(', '),
  },
};

const motionOk = '@media (prefers-reduced-motion: no-preference)';

const rise = keyframes`
  from { opacity: 0; transform: translate3d(0, 0.625rem, 0); }
`;

const drift = keyframes`
  from { transform: translate3d(0, 0, 0) scale(1); }
  to { transform: translate3d(3%, 0.75rem, 0) scale(1.07); }
`;

// On page change the cloud lights up: it fades in while growing from the top of the band, then settles into the drift.
const bloom = keyframes`
  from { opacity: 0; transform: translateX(-50%) scale(0.88); }
`;

// Cloud and art sit on a centred stage capped at 1760, so wide monitors don't stretch them.
const stageSx = {
  position: 'absolute',
  top: 0,
  left: '50%',
  width: '100%',
  maxWidth: 1760,
  transform: 'translateX(-50%)',
  pointerEvents: 'none',
  zIndex: 0,
} as const;

/** Side gutter shared by the band's copy and the page content under it (`pageContentSx` in apps/web). */
export const PAGE_GUTTER_X = { xs: 1, md: 3 } as const;

const bandSx = {
  position: 'relative',
  overflow: 'hidden',
  bgcolor: 'background.paper-elevation-1',
} as const;

const leadSx = {
  typography: { xs: 'body1', md: 'subtitle1' },
  maxWidth: 720,
  '& a': { color: 'text.primary', textDecoration: 'underline' },
} as const;

const actionsSx = {
  flexShrink: 0,
  '& > *': { flex: { xs: 1, md: 'none' } },
} as const;

export const PageHeader = ({
  tier = 'standard',
  eyebrow,
  title,
  lead,
  chips,
  actions,
  cloud = 'tools',
  art,
  maxWidth = 1180,
}: PageHeaderProps) => {
  const campaign = tier === 'campaign';
  const showCloud = !campaign || !art;

  return (
    <Box component="header" sx={bandSx}>
      {showCloud ? (
        <Box
          aria-hidden
          sx={{
            ...stageSx,
            height: { xs: 360, md: 480 },
            transformOrigin: '50% 0',
            maskImage: 'linear-gradient(to bottom, black 55%, transparent)',
            // Past the 1760 cap the band keeps going but the stage stops: fade its sides so the cut never shows on wide monitors.
            '@media (min-width: 1760px)': {
              maskImage:
                'linear-gradient(to bottom, black 55%, transparent), linear-gradient(to right, transparent, black 8%, black 92%, transparent)',
              maskComposite: 'intersect',
              WebkitMaskComposite: 'source-in',
            },
            [motionOk]: { animation: `${bloom} 1.2s cubic-bezier(0.2, 0.7, 0.2, 1) backwards` },
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: CLOUDS[cloud],
              [motionOk]: { animation: `${drift} 24s ease-in-out infinite alternate` },
            }}
          />
        </Box>
      ) : null}
      {campaign && !!art && (
        <Box aria-hidden sx={{ ...stageSx, bottom: 0, display: 'flex', justifyContent: 'flex-end' }}>
          <Box
            component="img"
            src={art.src}
            alt=""
            sx={{
              width: { xs: '100%', md: '50%' },
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'right center',
              // Below md the copy spans the band, so the art dims and fades out upwards: a glow in the lower right, behind the band rather than the title or lead.
              opacity: { xs: 0.55, md: 1 },
              maskImage: {
                xs: 'linear-gradient(to right, transparent 25%, black 75%), linear-gradient(to bottom, transparent 50%, black 95%)',
                md: 'linear-gradient(to right, transparent, black 45%, black 85%, transparent)',
              },
              maskComposite: { xs: 'intersect', md: 'add' },
              WebkitMaskComposite: { xs: 'source-in', md: 'source-over' },
            }}
          />
        </Box>
      )}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          maxWidth,
          mx: 'auto',
          px: PAGE_GUTTER_X,
          pt: campaign ? { xs: 3, md: 6 } : { xs: 2, md: 3 },
          pb: campaign ? { xs: 2.5, md: 4 } : { xs: 2, md: 3 },
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'flex-end' }}
          gap={2}
        >
          <Stack gap={1} sx={{ minWidth: 0 }}>
            <Typography variant="overline" component="div" color="text.secondary">
              {eyebrow}
            </Typography>
            <Typography
              variant={campaign ? 'h1' : 'h2'}
              component="h1"
              sx={{
                // Below md the title and lead step down one size on the ramp: a 28px title and a 16px lead crowd a phone.
                typography: campaign ? { xs: 'h2', md: 'h1' } : { xs: 'h3', md: 'h2' },
                [motionOk]: { animation: `${rise} 0.7s cubic-bezier(0.2, 0.7, 0.2, 1) backwards` },
              }}
            >
              {title}
            </Typography>
            {!!lead && (
              <Typography variant="subtitle1" component="div" color="text.secondary" sx={leadSx}>
                {lead}
              </Typography>
            )}
            {!!chips && (
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ pt: 1 }}>
                {chips}
              </Stack>
            )}
          </Stack>
          {!!actions && (
            <Stack direction="row" gap={1} sx={actionsSx}>
              {actions}
            </Stack>
          )}
        </Stack>
      </Box>
    </Box>
  );
};
